import React from 'react';

import { themeStore } from 'Models/ThemeStore';
import { useIntl } from 'react-intl';
import { Tag } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/CheckInList/Tag';
import { CustomTooltipWithCustomTrigger } from 'Views/Components/_HumanWritten/CustomTooltip/CustomToolTipWithCustomTrigger';
import { transactionListStatus } from './StaffTransactionsFilterStore';

export interface TransactionStatusTagProps {
	status: transactionListStatus;
}

export function TransactionStatusTag({ status }: TransactionStatusTagProps) {
	const intl = useIntl();
	let text = '';
	let customColour = '';
	let customTextColour = '';
	let hoverMessage = '';

	switch (status) {
		case 'FAILED':
			text = 'Failed';
			customColour = themeStore.cancelledTagColour;
			customTextColour = themeStore.textOnCancelledTag;
			hoverMessage = 'Payment failed. Associated bookings have been automatically cancelled.';
			break;
		case 'ABANDONED':
			text = 'Abandoned';
			customColour = '#bdbdbd';
			customTextColour = 'black';
			hoverMessage = 'Customer left checkout before completing payment. No charges were made.';
			break;
		case 'IN_PROGRESS':
			text = 'In Progress';
			customColour = '#71cdff';
			customTextColour = 'black';
			hoverMessage = 'This transaction is currently in progress.';
			break;
		case 'COMPLETED':
			text = intl.formatMessage({
				id: 'booking-status__paid__label',
				defaultMessage: 'Paid',
			});
			customColour = themeStore.paidTagColour;
			customTextColour = themeStore.textOnPaidTag;
			hoverMessage = 'Payment received in full via payment gateway.';
			break;
		case 'POS_PAYMENT':
			text = intl.formatMessage({ id: 'pos__status__label', defaultMessage: 'POS' });
			customColour = themeStore.paidTagColour;
			customTextColour = themeStore.textOnPaidTag;
			hoverMessage = intl.formatMessage({
				id: 'pos__status__description',
				defaultMessage: 'Payment received in full via integrated POS.',
			});
			break;
		case 'INVOICED':
			text = intl.formatMessage({
				id: 'booking-status__invoiced__label',
				defaultMessage: 'Invoiced',
			});
			customColour = themeStore.invoicedTagColour;
			customTextColour = themeStore.textOnInvoicedTag;
			hoverMessage = 'Invoice has been created in your external accounting software.';
			break;
		case 'PENDING_INVOICE':
			text = intl.formatMessage({
				id: 'booking-status__awaiting__label',
				defaultMessage: 'Awaiting',
			});
			customColour = themeStore.awaitingTagColour;
			customTextColour = themeStore.textOnAwaitingTag;
			hoverMessage = 'Invoice to be manually created in your external accounting software.';
			break;
		case 'EFTPOS':
			text = intl.formatMessage({
				id: 'eftpos__name__label',
				defaultMessage: 'Eftpos',
			});
			customColour = themeStore.paidTagColour;
			customTextColour = themeStore.textOnPaidTag;
			hoverMessage = `Payment received in full via ${intl.formatMessage({
				id: 'eftpos__name__label',
				defaultMessage: 'Eftpos',
			})}.`;
			break;
		case 'CASH':
			text = intl.formatMessage({
				id: 'cash-payment-option__label',
				defaultMessage: 'Cash',
			});
			customColour = themeStore.paidTagColour;
			customTextColour = themeStore.textOnPaidTag;
			hoverMessage = 'Payment received in full via cash.';
			break;
		case 'PENDING_PAYMENT':
			text = intl.formatMessage({
				id: 'booking-status__unpaid__label',
				defaultMessage: 'Unpaid',
			});
			customColour = themeStore.awaitingTagColour;
			customTextColour = themeStore.textOnInvoicedTag;
			hoverMessage = 'Awaiting payment. Booking is confirmed but payment will be collected later.';
			break;
	}

	return (
		<div className="tag__container">
			<CustomTooltipWithCustomTrigger
				trigger={
					<Tag
						text={text}
						customStyles={{
							background: customColour,
							color: customTextColour,
						}}
					/>
				}
				content={hoverMessage}
				toolTipPosition="bottom-right"
			/>
		</div>
	);
}
